import { useId } from 'react';
import type { ReactNode } from 'react';
import { WertEinheitFeld } from './WertEinheit';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FORMULARFELD (Auftrag 17.09.2026, Abschnitt 15; Abschnitt 28)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   „Text Input, Number Input, Select, Multi Select, Checkbox, Radio,
 *    Switch, Date Picker, Search, Unit Input, Textarea, Datei Upload,
 *    Validierung."
 *
 * Alle dreizehn Arten in EINEM Baustein, weil sie denselben Rahmen teilen:
 * Beschriftung über dem Feld, Pflichtstern, Hinweis darunter, Fehlertext
 * statt Hinweis, feste Feldbreite im Gitter. Wären es dreizehn Komponenten,
 * hätte spätestens die fünfte ihren Fehlertext anders gesetzt.
 *
 * Die Wertart hängt an `art` - `beiAenderung` liefert:
 *   text, suche, textfeld, datum, auswahl, radio   → `string`
 *   zahl                                           → `number | null`
 *   haken, schalter                                → `boolean`
 *   mehrfachauswahl                                → `string[]`
 *   datei                                          → `File[]`
 *
 * Das ist bewusst eine gemeinsame Vereinigungsmenge (`FeldWert`) und keine
 * generische Konstruktion: Die aufrufende Seite kennt ihre Art und schreibt
 * `(w) => setMenge(w as number | null)`. Eine Typakrobatik, die das erspart,
 * macht die Signatur für 147 Seiten unlesbar.
 *
 * `art: 'zahl'` MIT `einheit` reicht an `WertEinheitFeld` durch - dort steht
 * die Einheit in einem eigenen Element neben dem Feld und nie im
 * Platzhalter (Abschnitt 15).
 */

export type FeldArt =
  | 'text' | 'zahl' | 'auswahl' | 'mehrfachauswahl' | 'haken' | 'radio'
  | 'schalter' | 'datum' | 'suche' | 'textfeld' | 'datei';

export type FeldWert = string | number | boolean | string[] | File[] | null;

export interface FeldOption {
  wert: string;
  beschriftung: string;
  deaktiviert?: boolean;
  /** Kurze Erläuterung hinter der Beschriftung (nur bei Radio sichtbar). */
  erklaerung?: string;
}

export interface FeldProps {
  beschriftung: string;
  /** Vorgabe `text`. */
  art?: FeldArt;
  wert?: FeldWert;
  /**
   * Die Seite kennt ihre Art und setzt sie ein:
   * `beiAenderung={(w) => setMenge(w as number | null)}`.
   */
  beiAenderung?: (wert: FeldWert) => void;
  /** Auswahlmöglichkeiten für `auswahl`, `mehrfachauswahl`, `radio`. */
  optionen?: FeldOption[];
  /** Einheit - nur bei `art: 'zahl'`; steht neben dem Feld. */
  einheit?: string;
  nachkomma?: number;
  min?: number;
  max?: number;
  /** Platzhalter. Die Einheit gehört hier NICHT hinein (Abschnitt 15). */
  platzhalter?: string;
  /** Erläuterung unter dem Feld. */
  hinweis?: ReactNode;
  /** Fehlertext; setzt `aria-invalid` und ersetzt den Hinweis. */
  fehler?: string;
  pflicht?: boolean;
  deaktiviert?: boolean;
  nurLesen?: boolean;
  /** Zeilen eines Textfelds. Vorgabe 4. */
  zeilen?: number;
  /** Zulässige Dateiarten, z. B. `'.xlsx,.csv'`. */
  akzeptiert?: string;
  /** Mehrere Dateien zulassen. */
  mehrfach?: boolean;
  /** Breite im Feldgitter. Vorgabe `halb`. */
  breite?: 'voll' | 'halb' | 'drittel';
  id?: string;
  className?: string;
}

export function Feld({
  beschriftung, art = 'text', wert, beiAenderung, optionen = [],
  einheit, nachkomma, min, max, platzhalter, hinweis, fehler,
  pflicht, deaktiviert, nurLesen, zeilen = 4, akzeptiert, mehrfach,
  breite = 'halb', id, className,
}: FeldProps) {
  const eigeneId = useId();
  const feldId = id ?? `f-${eigeneId}`;
  const hinweisId = `${feldId}-hinweis`;
  const fehlerId = `${feldId}-fehler`;
  const beschriftungId = `${feldId}-beschriftung`;
  const melde = beiAenderung;

  const beschreibung = [
    hinweis ? hinweisId : null,
    fehler ? fehlerId : null,
  ].filter(Boolean).join(' ');

  // Die Zahl mit Einheit ist ein eigener Baustein - hier nur durchgereicht.
  if (art === 'zahl') {
    return (
      <WertEinheitFeld
        beschriftung={beschriftung}
        wert={typeof wert === 'number' ? wert : null}
        beiAenderung={melde as ((w: number | null) => void) | undefined}
        einheit={einheit}
        nachkomma={nachkomma}
        hinweis={typeof hinweis === 'string' ? hinweis : undefined}
        fehler={fehler}
        pflicht={pflicht}
        deaktiviert={deaktiviert}
        nurLesen={nurLesen}
        min={min}
        max={max}
        platzhalter={platzhalter}
        id={feldId}
        breite={breite}
      />
    );
  }

  /**
   * `beschriftungsArt` entscheidet, WIE die Beschriftung angebunden wird:
   *   'label'  ein `<label for>` - für jedes einzelne Steuerelement
   *   'gruppe' ein `<span id>`, auf das die Gruppe mit `aria-labelledby`
   *            zeigt - ein `<label for>` auf ein `<div>` ist ungültig
   *   'keine'  die Beschriftung steht im Steuerelement selbst (Haken)
   */
  const rahmen = (
    inhalt: ReactNode,
    beschriftungsArt: 'label' | 'gruppe' | 'keine' = 'label',
  ) => (
    <div
      className={[
        'm-feld', `m-feld-${breite}`,
        fehler ? 'm-feld-fehlerhaft' : '',
        className ?? '',
      ].filter(Boolean).join(' ')}
    >
      {beschriftungsArt === 'label' && (
        <label className="m-feld-beschriftung" id={beschriftungId} htmlFor={feldId}>
          {beschriftung}
          {pflicht && <span className="m-feld-pflicht" title="Pflichtangabe">*</span>}
        </label>
      )}
      {beschriftungsArt === 'gruppe' && (
        <span className="m-feld-beschriftung" id={beschriftungId}>
          {beschriftung}
          {pflicht && <span className="m-feld-pflicht" title="Pflichtangabe">*</span>}
        </span>
      )}
      {inhalt}
      {hinweis && !fehler && (
        <span className="m-feld-hinweis" id={hinweisId}>{hinweis}</span>
      )}
      {fehler && (
        <span className="m-feld-fehler" id={fehlerId} role="alert">
          <span aria-hidden="true">! </span>{fehler}
        </span>
      )}
    </div>
  );

  /** Was JEDES Steuerelement bekommt. */
  const gemeinsam = {
    id: feldId,
    disabled: deaktiviert,
    required: pflicht,
    'aria-invalid': fehler ? true : undefined,
    'aria-describedby': beschreibung || undefined,
  } as const;

  /** Zusätzlich für Text-Steuerelemente - `select` kennt kein `readOnly`. */
  const nurLesbar = { readOnly: nurLesen } as const;

  switch (art) {
    case 'textfeld':
      return rahmen(
        <textarea
          {...gemeinsam}
          {...nurLesbar}
          className="m-feld-eingabe"
          rows={zeilen}
          value={typeof wert === 'string' ? wert : ''}
          placeholder={platzhalter}
          onChange={(e) => melde?.(e.target.value)}
        />,
      );

    case 'auswahl':
      return rahmen(
        <select
          {...gemeinsam}
          className="m-feld-eingabe"
          value={typeof wert === 'string' ? wert : ''}
          onChange={(e) => melde?.(e.target.value)}
        >
          {platzhalter && <option value="">{platzhalter}</option>}
          {optionen.map((o) => (
            <option key={o.wert} value={o.wert} disabled={o.deaktiviert}>
              {o.beschriftung}
            </option>
          ))}
        </select>,
      );

    case 'mehrfachauswahl': {
      const gewaehlt = Array.isArray(wert) ? (wert as string[]) : [];
      return rahmen(
        // Kein `<select multiple>`: Das ist mit der Maus eine Falle (ein
        // Klick verwirft die ganze bisherige Auswahl). Eine Hakenliste ist
        // länger und dafür bedienbar.
        <div
          className="m-feld-hakenliste"
          role="group"
          aria-labelledby={beschriftungId}
          aria-describedby={beschreibung || undefined}
          id={feldId}
        >
          {optionen.map((o) => (
            <label key={o.wert} className="m-feld-haken">
              <input
                type="checkbox"
                value={o.wert}
                checked={gewaehlt.includes(o.wert)}
                disabled={deaktiviert || o.deaktiviert}
                onChange={() => melde?.(gewaehlt.includes(o.wert)
                  ? gewaehlt.filter((g) => g !== o.wert)
                  : [...gewaehlt, o.wert])}
              />
              {o.beschriftung}
            </label>
          ))}
        </div>,
        'gruppe',
      );
    }

    case 'radio':
      return rahmen(
        <div
          className="m-feld-radioliste"
          role="radiogroup"
          aria-labelledby={beschriftungId}
          aria-describedby={beschreibung || undefined}
          id={feldId}
        >
          {optionen.map((o) => (
            <label key={o.wert} className="m-feld-radio">
              <input
                type="radio"
                name={feldId}
                value={o.wert}
                checked={wert === o.wert}
                disabled={deaktiviert || o.deaktiviert}
                onChange={() => melde?.(o.wert)}
              />
              <span>
                {o.beschriftung}
                {o.erklaerung && (
                  <span className="m-feld-radio-erklaerung">{o.erklaerung}</span>
                )}
              </span>
            </label>
          ))}
        </div>,
        'gruppe',
      );

    case 'haken':
      return rahmen(
        <label className="m-feld-haken m-feld-haken-einzeln">
          <input
            {...gemeinsam}
            type="checkbox"
            checked={wert === true}
            onChange={(e) => melde?.(e.target.checked)}
          />
          <span>
            {beschriftung}
            {pflicht && <span className="m-feld-pflicht" title="Pflichtangabe">*</span>}
          </span>
        </label>,
        'keine',
      );

    case 'schalter':
      return rahmen(
        <button
          id={feldId}
          type="button"
          role="switch"
          aria-checked={wert === true}
          aria-describedby={beschreibung || undefined}
          className={`m-schalter${wert === true ? ' m-schalter-an' : ''}`}
          disabled={deaktiviert}
          onClick={() => melde?.(!(wert === true))}
        >
          <span className="m-schalter-bahn" aria-hidden="true">
            <span className="m-schalter-knebel" />
          </span>
          <span className="m-schalter-zustand">{wert === true ? 'ein' : 'aus'}</span>
        </button>,
      );

    case 'datei':
      return rahmen(
        <input
          {...gemeinsam}
          type="file"
          className="m-feld-eingabe m-feld-datei"
          accept={akzeptiert}
          multiple={mehrfach}
          onChange={(e) => melde?.([...(e.target.files ?? [])])}
        />,
      );

    case 'datum':
      return rahmen(
        <input
          {...gemeinsam}
          {...nurLesbar}
          type="date"
          className="m-feld-eingabe"
          value={typeof wert === 'string' ? wert : ''}
          onChange={(e) => melde?.(e.target.value)}
        />,
      );

    case 'suche':
      return rahmen(
        <input
          {...gemeinsam}
          {...nurLesbar}
          type="search"
          className="m-feld-eingabe m-feld-suche"
          value={typeof wert === 'string' ? wert : ''}
          placeholder={platzhalter ?? 'Suchen …'}
          onChange={(e) => melde?.(e.target.value)}
        />,
      );

    case 'text':
    default:
      return rahmen(
        <input
          {...gemeinsam}
          {...nurLesbar}
          type="text"
          className="m-feld-eingabe"
          value={typeof wert === 'string' ? wert : ''}
          placeholder={platzhalter}
          onChange={(e) => melde?.(e.target.value)}
        />,
      );
  }
}
